import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { styled } from '@mui/material/styles';
import { Stack, Slider, ButtonGroup, IconButton, Typography, } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import SettingsIcon from '@mui/icons-material/Settings';
import FullScreenIcon from '@mui/icons-material/Fullscreen';
import { grey, playerControllersColor, bgColorLight } from '../../tss/colors';
/**
 * Formats a number to always be 2 digits (e.g., 5 -> "05")
 */
const formatTime = (val) => `0${Math.floor(val)}`.slice(-2);
/**
 * Converts seconds to a formatted time string (MM:SS or HH:MM:SS)
 * @param seconds - The number of seconds to format
 * @returns Formatted time string
 *
 * @example
 * fancyTimeFormat(65) // "01:05"
 * fancyTimeFormat(3665) // "01:01:05"
 */
export const fancyTimeFormat = (seconds) => {
    if (!seconds) {
        return 0;
    }
    const hours = seconds / 3600;
    const minutes = (seconds % 3600) / 60;
    if (Math.floor(hours) !== 0) {
        return [hours, minutes, seconds % 60].map(formatTime).join(':');
    }
    return [minutes, seconds % 60].map(formatTime).join(':');
};
/**
 * Styled slider for video progress/seeking with custom colors and thumb
 */
const ProgressSlider = styled(Slider)({
    color: playerControllersColor,
    height: 3,
    '& .MuiSlider-track': {
        border: 'none',
    },
    '& .MuiSlider-thumb': {
        height: 12,
        width: 12,
        backgroundColor: playerControllersColor,
        border: '2px solid currentColor',
        '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
            boxShadow: 'inherit',
        },
        '&:before': {
            display: 'none',
        },
    },
    '& .MuiSlider-valueLabel': {
        lineHeight: 1.4,
        fontSize: 12,
        background: 'unset',
        padding: 0,
        width: 40,
        height: 40,
        borderRadius: '50% 50% 50% 0',
        backgroundColor: bgColorLight,
        transformOrigin: 'bottom left',
        transform: 'translate(50%, -100%) rotate(-45deg) scale(0)',
        '&:before': { display: 'none' },
        '&.MuiSlider-valueLabelOpen': {
            transform: 'translate(50%, -100%) rotate(-45deg) scale(1)',
        },
        '& > *': {
            transform: 'rotate(45deg)',
        },
    },
});
/**
 * Styled slider for volume control with custom styling
 */
const VolumeSlider = styled(Slider)({
    width: '80px',
    color: playerControllersColor,
    marginTop: 'auto !important',
    marginBottom: 'auto !important',
    height: 3,
    '& .MuiSlider-track': {
        border: 'none',
    },
    '& .MuiSlider-thumb': {
        height: 12,
        width: 12,
        backgroundColor: playerControllersColor,
        border: '2px solid currentColor',
        '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
            boxShadow: 'inherit',
        },
        '&:before': {
            display: 'none',
        },
    },
    '& .MuiSlider-valueLabel': {
        lineHeight: 1.4,
        fontSize: 12,
        fontWeight: 600,
        background: 'unset',
        padding: 0,
        width: 40,
        height: 20,
        // borderRadius: 20,
        backgroundColor: bgColorLight,
    },
});
/**
 * PlayerControls - Custom video player control bar
 *
 * Provides a full-featured control interface including:
 * - Play/Pause button
 * - Progress slider with time display
 * - Previous/Next navigation buttons
 * - Volume control with mute toggle
 * - Settings button (placeholder)
 * - Fullscreen toggle
 *
 * The controls overlay the bottom of the video with a semi-transparent background.
 * The progress slider supports seeking and displays the current time and total duration.
 * When maxAllowedValue is set, seeking is restricted to prevent skipping ahead.
 */
const PlayerControls = ({ playing, muted, volume, progress, isFullScreen, duration, setMuted, setVolume, setPlaying, handleNextPrevious, handleSeekStart, 
// handleSeekCommit,
handleClickFullscreen, disableSkipNext, maxAllowedValue, }) => (_jsxs(Stack, { direction: "row", spacing: 0.5, sx: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.6)',
        px: 1,
        borderRadius: 1,
        borderTopRightRadius: 1,
        borderTopLeftRadius: 1,
    }, children: [_jsx(IconButton, { sx: { color: playerControllersColor, p: 1 }, onClick: () => setPlaying(!playing), children: !playing ? _jsx(PlayArrowIcon, { fontSize: "large", sx: { color: grey } })
                : _jsx(PauseIcon, { fontSize: "large" }) }), _jsxs(Stack, { direction: "column", sx: { width: '100%' }, children: [_jsxs(Stack, { direction: "row", spacing: 1, children: [_jsx(ProgressSlider, { min: 0, max: duration, value: progress?.playedSeconds || 0, valueLabelDisplay: "auto", valueLabelFormat: (value) => fancyTimeFormat(value), onChange: (event, newValue) => {
                                const desiredValue = Array.isArray(newValue) ? newValue[0] : newValue;
                                handleSeekStart(Math.min(desiredValue, maxAllowedValue));
                            } }), _jsx(Typography, { component: "div", variant: "caption", sx: {
                                minWidth: '90px',
                                color: playerControllersColor,
                                textAlign: 'center',
                                lineHeight: 2.66,
                                fontSize: '10px',
                                fontWeight: 600,
                            }, children: _jsxs("time", { dateTime: `P${Math.round(duration)}S`, children: [progress?.playedSeconds ? fancyTimeFormat(progress?.playedSeconds) : '00:00', ' / ', duration ? fancyTimeFormat(duration) : '00:00'] }) })] }), _jsxs(Stack, { direction: "row", justifyContent: "space-between", children: [_jsxs(Stack, { direction: "row", spacing: 2, children: [_jsxs(ButtonGroup, { variant: "text", "aria-label": "buttons skip video", sx: { '& .MuiButtonBase-root': { borderRight: '0 !important' } }, children: [_jsx(IconButton, { onClick: () => handleNextPrevious('previous'), sx: { border: 'none', color: playerControllersColor }, children: _jsx(SkipPreviousIcon, { fontSize: "small" }) }), _jsx(IconButton, { disabled: disableSkipNext, onClick: () => handleNextPrevious('next'), sx: { color: playerControllersColor }, children: _jsx(SkipNextIcon, { fontSize: "small" }) })] }), _jsxs(Stack, { direction: "row", spacing: 1.5, children: [_jsx(IconButton, { sx: { color: playerControllersColor }, onClick: () => setMuted(!muted), children: muted ? _jsx(VolumeOffIcon, { fontSize: "small" }) : _jsx(VolumeUpIcon, { fontSize: "small" }) }), _jsx(VolumeSlider, { min: 0, max: 100, value: muted ? 0 : (volume || 0), valueLabelDisplay: "auto", valueLabelFormat: (value) => value, onChange: (event, newValue) => {
                                                if (muted)
                                                    setMuted(false);
                                                setVolume(Array.isArray(newValue) ? newValue[0] : newValue);
                                            } })] })] }), _jsxs(ButtonGroup, { variant: "text", "aria-label": "buttons skip video", sx: { '& .MuiButtonBase-root': { borderRight: '0 !important' } }, children: [_jsx(IconButton, { onClick: () => { }, sx: { border: 'none', color: playerControllersColor }, children: _jsx(SettingsIcon, { fontSize: "small" }) }), _jsx(IconButton, { sx: { color: playerControllersColor }, onClick: () => handleClickFullscreen(!isFullScreen, true), children: _jsx(FullScreenIcon, { fontSize: "small" }) })] })] })] })] }));
export default PlayerControls;
//# sourceMappingURL=PlayerControls.js.map